import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Star, ShoppingCart, Filter } from "lucide-react";
import { Link } from "react-router-dom";

interface Product {
  id: number;
  name: string;
  category: string;
  price: number;
  rating: number;
  image: string;
  inStock: boolean;
}

const allProducts: Product[] = [
  // Filters
  {
    id: 1,
    name: "RO Omgekeerde Osmose Filter",
    category: "Filter",
    price: 249.99,
    rating: 4.8,
    image: "🔵",
    inStock: true,
  },
  {
    id: 2,
    name: "Koolstof Water Filter",
    category: "Filter",
    price: 79.99,
    rating: 4.6,
    image: "⚫",
    inStock: true,
  },
  {
    id: 3,
    name: "Sediment Filter 5 Micron",
    category: "Filter",
    price: 49.99,
    rating: 4.5,
    image: "🟤",
    inStock: true,
  },
  {
    id: 4,
    name: "Ceramic Water Filter",
    category: "Filter",
    price: 129.99,
    rating: 4.7,
    image: "🟡",
    inStock: true,
  },
  // Kranen
  {
    id: 5,
    name: "Premium RVS Waterkraan",
    category: "Kraan",
    price: 189.99,
    rating: 4.9,
    image: "💧",
    inStock: true,
  },
  {
    id: 6,
    name: "Verchroomde Standaard Kraan",
    category: "Kraan",
    price: 89.99,
    rating: 4.4,
    image: "🚰",
    inStock: true,
  },
  {
    id: 7,
    name: "Dubbele Kanaal Waterkraan",
    category: "Kraan",
    price: 149.99,
    rating: 4.6,
    image: "⚙️",
    inStock: false,
  },
  // Accessoires
  {
    id: 8,
    name: "Silicone Slang Set (10m)",
    category: "Accessoire",
    price: 29.99,
    rating: 4.3,
    image: "🔧",
    inStock: true,
  },
  {
    id: 9,
    name: "Montage Kit Compleet",
    category: "Accessoire",
    price: 39.99,
    rating: 4.7,
    image: "🛠️",
    inStock: true,
  },
  {
    id: 10,
    name: "Filterpatroon Vervanging (3-pack)",
    category: "Accessoire",
    price: 59.99,
    rating: 4.8,
    image: "📦",
    inStock: true,
  },
  {
    id: 11,
    name: "Connector Set (5 stuks)",
    category: "Accessoire",
    price: 24.99,
    rating: 4.2,
    image: "⚡",
    inStock: true,
  },
  {
    id: 12,
    name: "Water Tank 50L",
    category: "Accessoire",
    price: 199.99,
    rating: 4.5,
    image: "🏺",
    inStock: true,
  },
];

export default function Shop() {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState("featured");
  const [searchQuery, setSearchQuery] = useState("");

  const categories = ["Filter", "Kraan", "Accessoire"];

  let filtered = selectedCategory
    ? allProducts.filter((p) => p.category === selectedCategory)
    : allProducts;

  if (searchQuery) {
    filtered = filtered.filter((p) =>
      p.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }

  if (sortBy === "price-low") {
    filtered = [...filtered].sort((a, b) => a.price - b.price);
  } else if (sortBy === "price-high") {
    filtered = [...filtered].sort((a, b) => b.price - a.price);
  } else if (sortBy === "rating") {
    filtered = [...filtered].sort((a, b) => b.rating - a.rating);
  }

  return (
    <main className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-primary/5 border-b py-8">
        <div className="max-w-7xl mx-auto px-4">
          <Link to="/" className="text-primary hover:underline text-sm mb-4 block">
            ← Terug naar Home
          </Link>
          <h1 className="text-4xl font-bold text-foreground mb-2">Onze Producten</h1>
          <p className="text-muted-foreground">
            {filtered.length} producten gevonden
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar - Filters */}
          <div className="lg:col-span-1">
            <div className="sticky top-20">
              {/* Search */}
              <div className="mb-6">
                <h3 className="font-bold mb-3 flex items-center gap-2">
                  <Filter className="w-4 h-4" />
                  Zoeken
                </h3>
                <Input
                  placeholder="Productnaam..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              {/* Categories */}
              <div className="mb-6">
                <h3 className="font-bold mb-3">Categoriëen</h3>
                <div className="space-y-2">
                  <Button
                    variant={selectedCategory === null ? "default" : "outline"}
                    size="sm"
                    className="w-full justify-start"
                    onClick={() => setSelectedCategory(null)}
                  >
                    Alles ({allProducts.length})
                  </Button>
                  {categories.map((cat) => {
                    const count = allProducts.filter((p) => p.category === cat).length;
                    return (
                      <Button
                        key={cat}
                        variant={selectedCategory === cat ? "default" : "outline"}
                        size="sm"
                        className="w-full justify-start"
                        onClick={() => setSelectedCategory(cat)}
                      >
                        {cat} ({count})
                      </Button>
                    );
                  })}
                </div>
              </div>

              {/* Sorting */}
              <div className="mb-6">
                <h3 className="font-bold mb-3">Sorteren</h3>
                <div className="space-y-2">
                  {[
                    { value: "featured", label: "Aanbevolen" },
                    { value: "price-low", label: "Prijs: Laag → Hoog" },
                    { value: "price-high", label: "Prijs: Hoog → Laag" },
                    { value: "rating", label: "Best Beoordeeld" },
                  ].map((option) => (
                    <Button
                      key={option.value}
                      variant={sortBy === option.value ? "default" : "outline"}
                      size="sm"
                      className="w-full justify-start"
                      onClick={() => setSortBy(option.value)}
                    >
                      {option.label}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Products Grid */}
          <div className="lg:col-span-3">
            {filtered.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-muted-foreground text-lg mb-4">
                  Geen producten gevonden
                </p>
                <Button onClick={() => setSearchQuery("")}>
                  Filters opnieuw instellen
                </Button>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filtered.map((product) => (
                  <Card
                    key={product.id}
                    className="overflow-hidden hover:shadow-lg transition-shadow flex flex-col"
                  >
                    <div className="h-48 bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center text-5xl relative">
                      {product.image}
                      {!product.inStock && (
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                          <span className="text-white font-bold text-sm">Uitverkocht</span>
                        </div>
                      )}
                    </div>
                    <div className="p-4 flex flex-col flex-grow">
                      <p className="text-xs text-primary font-semibold mb-1">
                        {product.category}
                      </p>
                      <h3 className="font-semibold text-foreground mb-2 text-sm line-clamp-2">
                        {product.name}
                      </h3>
                      <div className="flex items-center gap-1 mb-4">
                        <div className="flex">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`w-3 h-3 ${
                                i < Math.floor(product.rating)
                                  ? "fill-accent text-accent"
                                  : "text-muted"
                              }`}
                            />
                          ))}
                        </div>
                        <span className="text-xs text-muted-foreground">
                          ({product.rating})
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-2 mt-auto">
                        <span className="text-lg font-bold text-primary">
                          €{product.price.toFixed(2)}
                        </span>
                        <Button
                          size="sm"
                          variant={product.inStock ? "default" : "outline"}
                          disabled={!product.inStock}
                          className="gap-1"
                        >
                          <ShoppingCart className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
